import {
  MatPasswordStrengthComponent,
  MatPasswordStrengthInfoComponent,
} from '@angular-material-extensions/password-strength';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialogRef as MatDialogReference } from '@angular/material/dialog';
import { MatTab } from '@angular/material/tabs';
import { NGXLogger } from 'ngx-logger';
import { ZOOM_SETTINGS } from 'src/app/common/constants';

import { MatChipInput, MatChipInputEvent } from '@angular/material/chips';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { validColorValidator } from '@relynn/ngx-colors';
import { GenericFont } from 'common';
import { UserFriendlyError } from 'models';
import { derivedAsync } from 'ngxtension/derived-async';
import { tap } from 'rxjs';
import {
  NotificationPosition,
  ThemeMode,
  TypedControls,
  UserUiSettings,
} from 'src/app/models';
import { AppConfigQuery } from 'src/app/modules/app-config';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { LanguagesService } from 'src/app/services/languages.service';
import { NotificationService } from 'src/app/services/notification.service';
import { SupportedFeaturesService } from 'src/app/services/supported-features.service';
import { ApplicationService } from 'src/app/state/application/application.service';
import { ApplicationStore } from 'src/app/state/application/application.store';
import { TaskSearchesQuery } from 'src/app/state/task-searches/task-searches.query';

/** Describes a password form. */
interface UserPasswordForm {
  /** Current Password. */
  currentPassword: FormControl<string>;
  /** New Password. */
  newPassword: FormControl<string>;
}

/**
 * Typed user settings form from the {@link UserUiSettings} interface.
 *
 * This type also omits properties that are not exposed in this dialog.
 */
type UserSettingsForm = TypedControls<
  Omit<
    UserUiSettings,
    | 'currentLanguage'
    | 'deleteInboxDocumentOnInsert'
    | 'deleteSearchDocumentsOnInsert'
    | 'keepBaseSearchDocumentOnInsert'
  > & {
    // This property has to be omitted and re-added because the form and backing material control expect a string[] for multi-select purposes.
    currentLanguage: string[];
  }
>;

/** User Settings Dialog. */
@UntilDestroy()
@Component({
  selector: 'app-user-settings-dialog',
  templateUrl: './user-settings-dialog.component.html',
  styleUrls: ['./user-settings-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserSettingsDialogComponent implements OnInit, AfterViewInit {
  /** Password strength component reference. */
  @ViewChild('passwordComponent')
  passwordStrength: MatPasswordStrengthComponent;
  /** Password strength info component reference. */
  @ViewChild('passwordStrengthInfo')
  passwordStrengthInfo: MatPasswordStrengthInfoComponent;
  /** User tab. */
  @ViewChild('userTab')
  userTab: MatTab;
  /** Observable of whether to allow quests access to the viewer. */
  allowGuestSquare9Viewer$ = this.appConfigQuery.allowGuestSquare9Viewer$;
  /** An array of available fonts to be rendered in the mat-select. */
  availableFonts = Object.values(GenericFont);
  /** Custom password regex validation message. */
  customPasswordRegexMessage =
    this.appConfigQuery.appConfig.passwordRegexValidationMessage;
  /** Determines if there is a custom password regex. */
  customPasswordRegexProvided = this.appConfigQuery.customPasswordRegexProvided;
  /**
   * Custom stamps for the user.
   *
   * This is only for displaying the chips in the ui. The data is stored in the app state on save using the form control.
   */
  customStamps = signal<string[]>([]);
  /** If the internal viewer options should be hidden. */
  hideInternalViewerOptions = derivedAsync(
    () => this.appConfigQuery.hideFullViewer$
  );
  /** Determines if we should show the internal import settings. */
  internalImportsSupported = this.supportedFeatures.versionSupports(
    this.supportedFeatures.internalViewerImports
  );
  /** If the user is guest. */
  isGuest = this.auth.isGuest;
  /** Available languages. */
  languages$ = this.languagesService.getAvailableLanguages();
  /** Observable of the available task searches. */
  taskSearches$ = this.taskSearchesQuery.taskSearches$;
  /** Observable of whether task searches are disabled. */
  taskSearchesDisabled$ = this.appConfigQuery.disableGlobalActionTasks$;
  /** Available theme modes. */
  themeModes = ThemeMode;
  /** User password form. */
  userPasswordFormGroup: FormGroup<UserPasswordForm>;
  /** User settings form group. */
  userSettingsGroup: FormGroup<UserSettingsForm>;
  zoomSettings = ZOOM_SETTINGS;

  /**
   * Whether to disable refresh results when closing document tabs setting.
   *
   * @returns True if setting should be disabled or if the alwaysOpenNewTab setting is somehow missing from the form.
   */
  get disableRefreshResultsWhenClosingDocumentTabs(): boolean {
    return !this.userSettingsGroup.controls.alwaysOpenNewTab.value;
  }

  /**
   * Checks if the user profile update button should be disabled.
   *
   * @returns A boolean indicating if the button should be disabled.
   */
  get disableUserProfileUpdate(): boolean {
    return (
      this.userPasswordFormGroup.invalid ||
      this.passwordStrength.passwordFormControl.invalid ||
      // indicates that not all rules were met.
      (this.passwordStrength.passwordFormControl.value &&
        this.passwordStrength.passwordFormControl.value < 100) ||
      this.passwordStrength.passwordConfirmationFormControl.invalid ||
      this.userPasswordFormGroup.pristine
    );
  }

  /**
   * Whether to show the user tab.
   *
   * TODO the tab will need to show while hiding the password section if we add other user profile options such as settings sync to this tab.
   *
   * @returns A boolean.
   */
  get showUserTab(): boolean {
    if (!this.user || this.isGuest) {
      return false;
    }

    return this.user.provider === 'ui' && this.user.isInternalUser;
  }

  /**
   * Determines if the user tab is active.
   *
   * @returns A boolean.
   */
  get userTabIsActive(): boolean {
    return !!this.userTab && this.userTab.isActive;
  }

  private appState = this.applicationStore.getValue();
  private user = this.applicationStore.getValue().user;

  constructor(
    private logger: NGXLogger,
    private formBuilder: FormBuilder,
    private languagesService: LanguagesService,
    private dialogReference: MatDialogReference<UserSettingsDialogComponent>,
    private supportedFeatures: SupportedFeaturesService,
    private taskSearchesQuery: TaskSearchesQuery,
    private auth: AuthenticationService,
    private notify: NotificationService,
    private appConfigQuery: AppConfigQuery,
    private application: ApplicationService,
    private applicationStore: ApplicationStore
  ) {}

  ngAfterViewInit(): void {
    this.checkAndSetCustomPasswordRegex();
  }

  ngOnInit() {
    // Setup form controls
    this.userSettingsGroup = this.formBuilder.group<UserSettingsForm>({
      // Results
      searchCountEnabled: new FormControl<boolean>(
        this.appState.searchCountEnabled,
        { nonNullable: true }
      ),
      sortResultsArchivesByResultsCount: new FormControl<boolean>(
        this.appState.sortResultsArchivesByResultsCount,
        { nonNullable: true }
      ),
      hideArchivesWithNoResults: new FormControl<boolean>(
        this.appState.hideArchivesWithNoResults,
        { nonNullable: true }
      ),
      doubleClickOpen: new FormControl<boolean>(this.appState.doubleClickOpen, {
        nonNullable: true,
      }),
      usePreviousSearchCriteriaInRelatedSearch: new FormControl<boolean>(
        this.appState.usePreviousSearchCriteriaInRelatedSearch,
        { nonNullable: true }
      ),
      alwaysOpenNewTab: new FormControl<boolean>(
        this.appState.alwaysOpenNewTab,
        { nonNullable: true }
      ),
      refreshResultsWhenClosingDocumentTabs: new FormControl<boolean>(
        this.appState.refreshResultsWhenClosingDocumentTabs,
        { nonNullable: true }
      ),
      rightAlignNumericValuesInGrid: new FormControl<boolean>(
        this.appState.rightAlignNumericValuesInGrid,
        { nonNullable: true }
      ),
      archiveHistoryResultsPerPage: new FormControl<number>(
        this.appState.archiveHistoryResultsPerPage,
        {
          nonNullable: true,
          validators: [
            Validators.required,
            Validators.min(25),
            Validators.max(200),
          ],
        }
      ),
      archiveResultsPerPage: new FormControl<number>(
        this.appState.archiveResultsPerPage,
        {
          nonNullable: true,
          validators: [
            Validators.required,
            Validators.min(25),
            Validators.max(200),
          ],
        }
      ),
      defaultPageZoom: new FormControl<string>(this.appState.defaultPageZoom, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      showDomainInHistoryResults: new FormControl<boolean>(
        this.appState.showDomainInHistoryResults,
        { nonNullable: true }
      ),
      // Appearance
      themeMode: new FormControl<ThemeMode>(this.appState.themeMode, {
        nonNullable: true,
      }),
      useCompactMode: new FormControl<boolean>(this.appState.useCompactMode, {
        nonNullable: true,
      }),
      useBrowserLocaleToParseData: new FormControl<boolean>(
        this.appState.useBrowserLocaleToParseData,
        { nonNullable: true }
      ),
      // Sidebar
      showGlobalActionTasks: new FormControl<boolean>(
        this.appState.showGlobalActionTasks,
        { nonNullable: true }
      ),
      showFavoriteSearches: new FormControl<boolean>(
        this.appState.showFavoriteSearches,
        { nonNullable: true }
      ),
      showVersionsArchive: new FormControl<boolean>(
        this.appState.showVersionsArchive,
        { nonNullable: true }
      ),
      dropdownToTypeaheadConversionThreshold: new FormControl<number>(
        this.appState.dropdownToTypeaheadConversionThreshold,
        {
          nonNullable: true,
          validators: [Validators.required, Validators.pattern('^-?[0-9]*$')],
        }
      ),
      enabledTasksList: new FormControl<string[]>(
        this.appState.enabledTasksList,
        { nonNullable: true }
      ),
      //Keyfree
      keyfreeUseOcr: new FormControl<boolean>(this.appState.keyfreeUseOcr, {
        nonNullable: true,
      }),
      keyfreeAutoOcr: new FormControl<boolean>(this.appState.keyfreeAutoOcr, {
        nonNullable: true,
      }),
      keyfreeAutoStartOnDocumentLoad: new FormControl<boolean>(
        this.appState.keyfreeAutoStartOnDocumentLoad,
        {
          nonNullable: true,
        }
      ),
      // Language, has to go into an array due to fancy select control
      currentLanguage: new FormControl<string[]>(
        [this.appState.currentLanguage],
        { nonNullable: true }
      ),
      // Viewer
      viewerAutoSave: new FormControl<boolean>(this.appState.viewerAutoSave, {
        nonNullable: true,
      }),
      autoOpenFirstTableFieldOnIndexerLoad: new FormControl<boolean>(
        this.appState.autoOpenFirstTableFieldOnIndexerLoad,
        { nonNullable: true }
      ),
      autoSave: new FormControl<boolean>(this.appState.autoSave, {
        nonNullable: true,
      }),
      autoSaveOnVersioningArchives: new FormControl<boolean>(
        this.appState.autoSaveOnVersioningArchives,
        { nonNullable: true }
      ),
      indexerReloadAfterSave: new FormControl<boolean>(
        this.appState.indexerReloadAfterSave,
        {
          nonNullable: true,
        }
      ),
      internalViewerClearCacheOnReturnToSearch: new FormControl<boolean>(
        this.appState.internalViewerClearCacheOnReturnToSearch,
        { nonNullable: true }
      ),
      persistArchiveImportData: new FormControl<boolean>(
        this.appState.persistArchiveImportData,
        { nonNullable: true }
      ),
      viewerOpenTableFieldsInSidebar: new FormControl<boolean>(
        this.appState.viewerOpenTableFieldsInSidebar,
        { nonNullable: true }
      ),
      showTableCellEditorButtons: new FormControl<boolean>(
        this.appState.showTableCellEditorButtons,
        { nonNullable: true }
      ),
      viewerUseInternal: new FormControl<boolean>(
        this.appState.viewerUseInternal,
        { nonNullable: true }
      ),
      viewerUseInternalForImport: new FormControl<boolean>(
        this.appState.viewerUseInternalForImport,
        { nonNullable: true }
      ),
      dxcAppendToMultivalue: new FormControl<boolean>(
        this.appState.dxcAppendToMultivalue,
        { nonNullable: true }
      ),
      dxcSearchSystemFields: new FormControl<boolean>(
        this.appState.dxcSearchSystemFields,
        { nonNullable: true }
      ),
      annotationNoteDefaultFillColor: new FormControl<string>(
        this.appState.annotationNoteDefaultFillColor,
        {
          nonNullable: true,
          validators: [Validators.required, validColorValidator()],
        }
      ),
      annotationNoteDefaultFontName: new FormControl<GenericFont>(
        this.appState.annotationNoteDefaultFontName,
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      annotationNoteDefaultFontSize: new FormControl<number>(
        this.appState.annotationNoteDefaultFontSize,
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      annotationNoteDefaultOutlineOpacity: new FormControl<number>(
        this.appState.annotationNoteDefaultOutlineOpacity,
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      annotationStampDefaultColor: new FormControl<string>(
        this.appState.annotationStampDefaultColor,
        {
          nonNullable: true,
          validators: [Validators.required, validColorValidator()],
        }
      ),
      annotationStampDefaultOpacity: new FormControl<number>(
        this.appState.annotationStampDefaultOpacity,
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      annotationStampDefaultThickness: new FormControl<number>(
        this.appState.annotationStampDefaultThickness,
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      customStamps: new FormControl<string[]>(this.appState.customStamps, {
        nonNullable: true,
      }),
      showTableFieldColumnsInGrid: new FormControl<boolean>(
        this.appState.showTableFieldColumnsInGrid,
        { nonNullable: true }
      ),
      autoSizeTextAreasToContentOnFocus: new FormControl<boolean>(
        this.appState.autoSizeTextAreasToContentOnFocus,
        { nonNullable: true }
      ),
      showContentSearchTermsInPdfPreview: new FormControl<boolean>(
        this.appState.showContentSearchTermsInPdfPreview,
        { nonNullable: true }
      ),
      notificationPosition: new FormControl<NotificationPosition>(
        this.appState.notificationPosition,
        { nonNullable: true }
      ),
    });

    // Sync chip group with app state. We must deference the array as well to avoid conflicts with immutable akita state.
    this.customStamps.set([...this.appState.customStamps]);

    // We need to listen for changes to color controls to ensure the color picker and inputs are synced
    this.userSettingsGroup.controls.annotationNoteDefaultFillColor.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.userSettingsGroup.controls.annotationNoteDefaultFillColor.setValue(
          value,
          {
            onlySelf: true,
            emitEvent: false,
            emitModelToViewChange: true,
          }
        );
      });

    this.userPasswordFormGroup = new FormGroup<UserPasswordForm>({
      currentPassword: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      newPassword: new FormControl<string>('', { nonNullable: true }),
    });

    // Disable/enable values on changes:
    const alwaysOpenNewTabControl =
      this.userSettingsGroup.controls.alwaysOpenNewTab;
    alwaysOpenNewTabControl.valueChanges
      .pipe(
        untilDestroyed(this),
        tap((value) => {
          if (value) {
            this.userSettingsGroup.controls.refreshResultsWhenClosingDocumentTabs.enable();
          } else {
            this.userSettingsGroup.controls.refreshResultsWhenClosingDocumentTabs.disable();
          }
        })
      )
      .subscribe();
    alwaysOpenNewTabControl.updateValueAndValidity();
    const searchCountEnabledControl =
      this.userSettingsGroup.controls.searchCountEnabled;
    searchCountEnabledControl.valueChanges
      .pipe(
        untilDestroyed(this),
        tap((value) => {
          if (value) {
            this.userSettingsGroup.controls.sortResultsArchivesByResultsCount.enable();
            this.userSettingsGroup.controls.hideArchivesWithNoResults.enable();
          } else {
            this.userSettingsGroup.controls.sortResultsArchivesByResultsCount.disable();
            this.userSettingsGroup.controls.hideArchivesWithNoResults.disable();
          }
        })
      )
      .subscribe();
    searchCountEnabledControl.updateValueAndValidity();
    // When keyfree ocr is enabled we disable the auto ocr option and set it to false.
    const keyfreeUseOcrControl = this.userSettingsGroup.controls.keyfreeUseOcr;
    keyfreeUseOcrControl.valueChanges
      .pipe(
        untilDestroyed(this),
        tap((value) => {
          if (value) {
            const keyFreeAutoOcrControl =
              this.userSettingsGroup.controls.keyfreeAutoOcr;
            keyFreeAutoOcrControl.disable();
            keyFreeAutoOcrControl.setValue(false);
          } else {
            this.userSettingsGroup.controls.keyfreeAutoOcr.enable();
          }
        })
      )
      .subscribe();
    keyfreeUseOcrControl.updateValueAndValidity();

    if (!this.userSettingsGroup.controls.autoSave.value) {
      this.userSettingsGroup.controls.autoSaveOnVersioningArchives.disable();
    }
  }

  /**
   * Handler for the add chip event on custom stamps.
   *
   * @param event Chip event containing the new value.
   */
  addCustomStamp(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (this.customStamps().includes(value)) {
      this.logger.debug(`The custom stamp '${value} already exists.`);
      // Clear the input value but skip adding the stamp.
      event.chipInput!.clear();
      return;
    }
    this.customStamps.update((stamps) => {
      stamps.push(value);
      return stamps;
    });

    // Clear the input value
    event.chipInput!.clear();
  }

  /**
   * Handler for the add custom stamp button click.
   *
   * @param event Chip input element.
   */
  addCustomStampButtonClicked(chipInput: MatChipInput): void {
    const value = (chipInput.inputElement.value || '').trim();
    if (this.customStamps().includes(value)) {
      this.logger.debug(`The custom stamp '${value} already exists.`);
      // Clear the input value but skip adding the stamp.
      chipInput.clear();
      return;
    }

    this.customStamps.update((stamps) => {
      stamps.push(value);
      return stamps;
    });

    // Clear the input value
    chipInput!.clear();
    // Ensure the input is focused for further input.
    chipInput.focus();
  }

  /**
   * Handler for the remove chip event on custom stamps.
   *
   * @param stampText Stamp to be removed.
   */
  removeCustomStamp(stampText: string): void {
    this.customStamps.update((stamps) => {
      const index = stamps.indexOf(stampText);
      if (index !== -1) {
        stamps.splice(index, 1);
      }
      return stamps;
    });
  }

  /**
   * Reset event handler.
   */
  onReset(): void {
    this.logger.debug(
      'Reset button clicked. Resetting user settings to default.'
    );
    this.application.resetUserSettingsToDefault();
    this.dialogReference.close();
  }

  isTenantAdmin(): boolean {
    return this.user?.name.toLowerCase() === 'tenantadmin';
  }

  openTenantLink(): void {
    window.open('https://square9.to/cloud-admin-password', '_blank');
  }

  /**
   * Handler for the auto save checkbox change event.
   *
   * @param event Checkbox change event.
   */
  onAutoSaveCheckChange(event: MatCheckboxChange): void {
    if (event.checked) {
      // The legacy auto save and new auto save cannot both be enabled.
      this.userSettingsGroup.controls.viewerAutoSave.setValue(false);
      this.userSettingsGroup.controls.autoSaveOnVersioningArchives.enable();
    } else {
      this.userSettingsGroup.controls.autoSaveOnVersioningArchives.setValue(
        false
      );
      this.userSettingsGroup.controls.autoSaveOnVersioningArchives.disable();
    }
  }

  /**
   * Handler for the legacy auto save checkbox change event.
   *
   * @param event Checkbox change event.
   */
  onLegacyAutoSaveCheckChange(event: MatCheckboxChange): void {
    if (event.checked) {
      // The legacy auto save and new auto save cannot both be enabled.
      this.userSettingsGroup.controls.autoSave.setValue(false);
      this.userSettingsGroup.controls.autoSaveOnVersioningArchives.setValue(
        false
      );
      this.userSettingsGroup.controls.autoSaveOnVersioningArchives.disable();
    }
  }

  /**
   * Save event handler.
   */
  onSave() {
    // Get the raw form value to ensure all control values (including those that are disabled) are retrieved.
    const settingsValues = this.userSettingsGroup.getRawValue();
    // Collect settings
    const savedSettings: Partial<UserUiSettings> = {
      ...settingsValues,
      // flatten the current language selection since the control returns an array.
      currentLanguage: settingsValues.currentLanguage
        ? settingsValues.currentLanguage[0]
        : 'English',
    };
    this.application.applyUserUiSettings(savedSettings);
    this.logger.debug('Settings saved.', settingsValues);
    this.dialogReference.close();
  }

  /**
   * Handler for the show global action tasks checkbox change event.
   *
   * @param event Checkbox change event.
   */
  onShowGlobalActionTasksChange(event: MatCheckboxChange): void {
    if (!event.checked) {
      this.logger.debug(
        'Global action tasks are disabled. Disabling all enabled queues.'
      );
      this.userSettingsGroup.controls.enabledTasksList.setValue([]);
    }
  }

  /** Password update event handler. */
  onUpdatePassword(): void {
    this.logger.debug('Update password clicked.');
    // TODO this will log the user out. Should we prompt to confirm?
    const formValues = this.userPasswordFormGroup.value;
    if (!formValues.newPassword) {
      this.logger.warn('No new password was provided.');
      return;
    }
    if (!formValues.currentPassword) {
      this.logger.warn('Current password was not provided.');
      return;
    }
    this.auth
      .changePassword(formValues.currentPassword, formValues.newPassword)
      .subscribe({
        next: () => {
          this.notify.success('PASSWORD_CHANGE_SUCCESS');
          this.dialogReference.close();
        },
        error: (error: UserFriendlyError) => this.notify.error(error),
      });
  }

  private checkAndSetCustomPasswordRegex(): void {
    if (this.appConfigQuery.customPasswordRegexProvided) {
      this.passwordStrength.customValidator = new RegExp(
        this.appConfigQuery.appConfig.passwordRegex
      );

      this.passwordStrength.setRulesAndValidators();
      this.passwordStrengthInfo.customCharsCriteriaMsg =
        this.appConfigQuery.appConfig.passwordRegexValidationMessage;
    }
  }
}
